import "./App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, lazy, Suspense } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ImportantNoticeModal from "./components/ImportantNoticeModal";
import Home from "./pages/HomePremium";
import About from "./pages/AboutUltra";
import Compliance from "./pages/ComplianceUltra";
import Careers from "./pages/CareersUltra";
import Contact from "./pages/ContactUltra";

import { AdminAuthProvider } from "./admin/AdminAuthContext";
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/AdminDashboard";
import AdminApplications from "./admin/AdminApplications";
import AdminContacts from "./admin/AdminContacts";
import AdminInterviews from "./admin/AdminInterviews";
import AdminEmailCenter from "./admin/AdminEmailCenter";

// Lazy-loaded heavy modules for runtime optimization
const AdminActivities = lazy(() => import("./admin/AdminActivities"));
const AdminAuditLogs = lazy(() => import("./admin/AdminAuditLogs"));
const ReportsArchive = lazy(() => import("./admin/ReportsArchive"));
const SystemDiagnostics = lazy(() => import("./admin/SystemDiagnostics"));

const LazyFallback = () => (
  <div className="p-8 flex items-center justify-center min-h-[300px]">
    <div className="flex items-center gap-3 text-cyan-300 font-mono text-sm animate-pulse">
      <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      <span>Initializing Enterprise Module...</span>
    </div>
  </div>
);

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

// Navigation and Router Instrumentation Observer Component
function RouterTraceObserver() {
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname + location.search);

  useEffect(() => {
    const prevRoute = prevLocationRef.current;
    const currentRoute = location.pathname + location.search;
    console.debug("[GLOBAL ROUTER LOCATION CHANGE]", {
      currentPathname: location.pathname,
      searchParams: location.search,
      previousRoute: prevRoute,
      nextRoute: currentRoute,
      timestamp: new Date().toISOString()
    });
    prevLocationRef.current = currentRoute;

    // STEP 2 Section 9 - DOM Overlay Detection
    setTimeout(() => {
      try {
        const allElements = document.body.querySelectorAll("*");
        allElements.forEach((el) => {
          const style = window.getComputedStyle(el);
          if (
            (style.position === "fixed" || style.position === "absolute") &&
            style.pointerEvents !== "none" &&
            style.display !== "none" &&
            style.visibility !== "hidden"
          ) {
            const rect = el.getBoundingClientRect();
            const zIndex = parseInt(style.zIndex, 10) || 0;
            // Check if element covers most of viewport or has high z-index with low opacity
            const coversViewport = rect.width >= window.innerWidth * 0.8 && rect.height >= window.innerHeight * 0.8;
            if (coversViewport || zIndex > 100 || parseFloat(style.opacity) === 0) {
              console.debug("[OVERLAY DETECTED]", {
                tagName: el.tagName,
                className: el.className,
                id: el.id,
                position: style.position,
                zIndex: style.zIndex,
                pointerEvents: style.pointerEvents,
                opacity: style.opacity,
                visibility: style.visibility,
                display: style.display,
                boundingRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
                timestamp: new Date().toISOString()
              });
            }
          }
        });
      } catch (e) {
        // Overlay detection check error ignored
      }
    }, 300);
  }, [location]);

  return null;
}

// ScrollToTop component to handle page navigation
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function InstrumentedRouteWrapper({ componentName, element }) {
  const location = useLocation();
  console.log("WRAPPER", componentName, location.pathname);
  useEffect(() => {
    const start = performance.now();
    console.debug(`[RENDER STARTED: ${componentName}]`, {
      pathname: location.pathname,
      timestamp: new Date().toISOString()
    });
    return () => {
      const duration = (performance.now() - start).toFixed(2);
      console.debug(`[RENDER COMPLETED: ${componentName}]`, {
        durationMs: duration,
        pathname: location.pathname,
        timestamp: new Date().toISOString()
      });
    };
  }, [componentName, location.pathname]);

  return element;
}

function PublicShell() {
  const location = useLocation();
  console.debug("[PUBLIC SHELL OUTLET RENDER]", { pathname: location.pathname, timestamp: new Date().toISOString() });
  return (
    <>
      <ImportantNoticeModal />
      <Header />
      <main className="main-content">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  console.log("APP_ROUTES_RENDER", location.pathname);
  console.debug("[APP ROUTES RENDER]", { timestamp: new Date().toISOString() });
  return (
    <Routes>
      {/* Public Routes with PublicShell Layout */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/* Admin Login & Portal Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="applications" element={<AdminApplications />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="interviews" element={<AdminInterviews />} />
        <Route path="emails" element={<AdminEmailCenter />} />
        <Route path="activities" element={<Suspense fallback={<LazyFallback />}><AdminActivities /></Suspense>} />
        <Route path="audit-logs" element={<Suspense fallback={<LazyFallback />}><AdminAuditLogs /></Suspense>} />
        <Route path="reports-archive" element={<Suspense fallback={<LazyFallback />}><ReportsArchive /></Suspense>} />
        <Route path="diagnostics" element={<Suspense fallback={<LazyFallback />}><SystemDiagnostics /></Suspense>} />
      </Route>
    </Routes>
  );
}

function App() {
  console.debug("[APP COMPONENT RENDER]", { timestamp: new Date().toISOString() });
  const appContent = (
    <div className="App">
      <BrowserRouter>
        <AdminAuthProvider>
          <RouterTraceObserver />
          <ScrollToTop />
          <AppRoutes />
        </AdminAuthProvider>
      </BrowserRouter>
    </div>
  );

  if (!RECAPTCHA_SITE_KEY) return appContent;

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{ async: true, defer: true, appendTo: "head" }}
      container={{ parameters: { badge: "bottomright" } }}
    >
      {appContent}
    </GoogleReCaptchaProvider>
  );
}

export default App;

